import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { StorageService } from '../storage/storage.service';
import { TranslocoService, getBrowserLang, getBrowserCultureLang } from '@jsverse/transloco';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subject, takeUntil } from 'rxjs';
import { HttpService } from '../http/http.service';
import { SentryService } from '../analytics/sentry.service';

const SELECTED_LANGUAGE_KEY = 'selected_language_key';

export enum LanguageType {
  English = 'English',
  Finnish = 'Finnish',
  French = 'French',
  German = 'German',
  PortugueseBrazil = 'Portuguese (Brazil)',
  Spanish = 'Spanish',
  Russian = 'Russian',
}

interface LanguageMapping {
  isoCode: string;
  regionVariant?: string;
  fileCode: string;
}

interface SupportedLanguage {
  name: LanguageType;
  languageCode: LanguageMapping;
}

const DefaultLanguage: SupportedLanguage = {
  name: LanguageType.English,
  languageCode: { isoCode: 'en', fileCode: 'en' },
};

@Injectable({
  providedIn: 'root',
})
export class TranslationService implements OnDestroy {
  private storageService = inject(StorageService);
  private translocoService = inject(TranslocoService);
  private httpService = inject(HttpService);
  private sentryService = inject(SentryService);

  private supportedLanguages: SupportedLanguage[];
  private chosenLanguageSignal = signal<string | undefined>(undefined);
  private destroy$ = new Subject<void>();

  constructor() {
    this.supportedLanguages = [
      DefaultLanguage,
      {
        name: LanguageType.Finnish,
        languageCode: { isoCode: 'fi', fileCode: 'fi' },
      },
      {
        name: LanguageType.French,
        languageCode: { isoCode: 'fr', fileCode: 'fr' },
      },
      {
        name: LanguageType.German,
        languageCode: { isoCode: 'de', fileCode: 'de' },
      },
      {
        name: LanguageType.PortugueseBrazil,
        languageCode: { isoCode: 'pt', regionVariant: 'pt-BR', fileCode: 'pt_BR' },
      },
      {
        name: LanguageType.Spanish,
        languageCode: { isoCode: 'es', fileCode: 'es' },
      },
      {
        name: LanguageType.Russian,
        languageCode: { isoCode: 'ru', fileCode: 'ru' },
      },
    ];

    toObservable(this.chosenLanguageSignal)
      .pipe(takeUntil(this.destroy$))
      .subscribe((chosenLanguage) => {
        if (chosenLanguage) {
          this.httpService.setLanguage(chosenLanguage);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLanguageNameFromIso(isoCode: string): string | undefined {
    const foundLang = this.supportedLanguages.find(
      (supportedLang) => supportedLang.languageCode.isoCode === isoCode,
    );
    return foundLang ? foundLang.name : undefined;
  }

  async currentLanguage(): Promise<string> {
    const selectedLanguage = await this.storageService.getItem<string>(SELECTED_LANGUAGE_KEY);
    return selectedLanguage !== null ? selectedLanguage : DefaultLanguage.name;
  }

  async currentLanguageIso(): Promise<string> {
    const selectedLanguage = await this.storageService.getItem<string>(SELECTED_LANGUAGE_KEY);
    const matched = this.supportedLanguages.find((lang) => lang.name === selectedLanguage);
    return matched?.languageCode.isoCode ?? DefaultLanguage.languageCode.isoCode;
  }

  async preloadLanguage(): Promise<void> {
    const selectedLanguage = await this.storageService.getItem<string>(SELECTED_LANGUAGE_KEY);
    if (await this.switchLanguage(selectedLanguage)) {
      return;
    }

    const browserLang = getBrowserLang();
    const foundLang = this.supportedLanguages.find((supportedLang) => {
      return supportedLang.languageCode.isoCode === browserLang;
    });
    await this.setLanguage(foundLang);
  }

  async switchLanguage(langName: string): Promise<boolean> {
    const foundLang = this.supportedLanguages.find((supportedLang) => {
      return supportedLang.name === langName;
    });
    if (foundLang === undefined) {
      return false;
    }
    await this.setLanguage(foundLang);
    return true;
  }

  private async setLanguage(supportedLanguage?: SupportedLanguage): Promise<void> {
    if (supportedLanguage === undefined) {
      await this.setActiveLanguage(DefaultLanguage);
      return;
    }

    if (supportedLanguage.languageCode.regionVariant) {
      const cultureLang = getBrowserCultureLang();
      if (supportedLanguage.languageCode.regionVariant === cultureLang) {
        await this.setActiveLanguage(supportedLanguage);
        return;
      }
    }

    await this.setActiveLanguage(supportedLanguage);
  }

  private async setActiveLanguage(supportedLanguage: SupportedLanguage): Promise<void> {
    if (
      !this.translocoService
        .getAvailableLangs()
        .some((availableLang) => availableLang === supportedLanguage.languageCode.fileCode)
    ) {
      // Set to English (Default), don't set in storage as we might add further languages
      // in the future and they will automatically be picked up.
      this.translocoService.setActiveLang(DefaultLanguage.languageCode.fileCode);
      this.chosenLanguageSignal.set(DefaultLanguage.name);
      return;
    }

    this.translocoService.setActiveLang(supportedLanguage.languageCode.fileCode);
    this.chosenLanguageSignal.set(supportedLanguage.name);
    this.sentryService.setActiveLanguage(supportedLanguage.name);
    await this.storageService.setItem<string>(SELECTED_LANGUAGE_KEY, supportedLanguage.name);
  }
}
